import type {
  TValuesCurrentBlocks,
  TValuesCurrentHeatmapPoints,
  TValuesCurrentPoints,
  TValuesCurrentProperties,
  TValuesCurrentRegions,
} from 'App/Map/types'
import type { TPointCategory } from 'stores/selectedFieldAssetsStore/selectedFieldAssetsStore'
import {
  degreeDaysRecord,
  larvalTrendRecord,
  phenologyRecord,
  trapCatchesRecord,
} from 'stores/selectedValueGroupsStore/insectValueGroupRecords'
import type { TValueGroup } from 'stores/selectedValueGroupsStore/selectedValueGroupsStore'
import type { M_ITEM } from './types'
import { MAP_VISUAL } from './types'

const emptyInsectDegreeDays = degreeDaysRecord(() => null)
const emptyInsectLarvalTrend = larvalTrendRecord(() => null)
const emptyInsectTrapCatches = trapCatchesRecord(() => null)
const emptyInsectPhenology = phenologyRecord(() => null)

const emptyNonInsects = {
  air_temperature: null,
  almond_bloom: null,
  almond_hull_split: null,
  alternaria: null,
  atmospheric_pressure: null,
  bee_hours: null,
  cherry_powdery_mildew: null,
  chill: null,
  conditions: null,
  dew_point: null,
  equipment_status: null,
  evapotranspiration: null,
  fire_blight: null,
  ndvi: null,
  fruit_growth: null,
  grape_powdery_mildew: null,
  plant_stress: null,
  humidity: null,
  irrigation_activity_pressure: null,
  irrigation_activity_zones: null,
  irrigation_activity_flow_meters: null,
  leaf_wetness: null,
  precipitation: null,
  soil: null,
  spray_conditions: null,
  wet_bulb: null,
  wind: null,
  wind_machine: null,
}

const initialStateDegreeDays = (insectId: number, mItem: M_ITEM) => ({
  valueType: `insectDegreeDays_sinceBiofixSum_insectId${insectId}_degreeDaysC` as TValuesCurrentPoints &
    TValuesCurrentHeatmapPoints,
  mItem,
  pointCategory: 'weatherPoint' as TPointCategory,
})

const initialStateTrapCatches = (insectId: number, mItem: M_ITEM) => ({
  valueType: `insectTrapCatches_todaySum_insectId${insectId}` as TValuesCurrentPoints & TValuesCurrentBlocks,
  mItem,
  pointCategory: 'trapPoint' as TPointCategory,
})

const initialStateTrapCatchesProperty = (insectId: number, mItem: M_ITEM) => ({
  valueType: `insectTrapCatches_todaySum_insectId${insectId}` as TValuesCurrentProperties,
  mItem,
  pointCategory: 'trapPoint' as TPointCategory,
})

const initialStateDegreeDaysSpatial = (insectId: number, mItem: M_ITEM) => ({
  valueType:
    `insectDegreeDays_sinceBiofixSumMinMedianMax_insectId${insectId}_degreeDaysC` as TValuesCurrentBlocks &
      TValuesCurrentProperties &
      TValuesCurrentRegions,
  mItem,
})

const initialStateDegreeDaysSpatialProperty = (insectId: number, mItem: M_ITEM) => ({
  valueType:
    `insectDegreeDays_sinceBiofixSumMinMedianMax_insectId${insectId}_degreeDaysC` as TValuesCurrentProperties,
  mItem,
})

type TVisGroup<TValueType> = Record<
  TValueGroup,
  {
    valueType: TValueType | null
    mItem: M_ITEM
    pointCategory?: TPointCategory
    outOfBlockValueType?: TValueType | null
  } | null
>

export type TMapVisualValueGroup = {
  [MAP_VISUAL.BLOCK]: TVisGroup<TValuesCurrentBlocks>
  [MAP_VISUAL.PROPERTY]: TVisGroup<TValuesCurrentProperties>
  [MAP_VISUAL.POINT]: TVisGroup<TValuesCurrentPoints>
  [MAP_VISUAL.SCD]: TVisGroup<TValuesCurrentHeatmapPoints>
  [MAP_VISUAL.TRAP]: TVisGroup<TValuesCurrentPoints>
  [MAP_VISUAL.REGION]: TVisGroup<TValuesCurrentRegions>
  [MAP_VISUAL.IRRIGATION_ZONE]: TVisGroup<TValuesCurrentPoints>
  [MAP_VISUAL.IMAGE_OVERLAY]: TVisGroup<TValuesCurrentBlocks>
}

export const defaultMapVisualValueGroup: TMapVisualValueGroup = {
  [MAP_VISUAL.POINT]: {
    ...emptyNonInsects,
    ...emptyInsectDegreeDays,
    ...emptyInsectLarvalTrend,
    ...emptyInsectTrapCatches,
    air_temperature: {
      valueType: 'temperature_in_c',
      mItem: 'M_InBlockPoint',
      pointCategory: 'weatherPoint',
      outOfBlockValueType: 'temperature_out_c',
    },
    almond_hull_split: {
      valueType: 'almondHullSplit_date',
      mItem: 'M_InBlockPoint',
      pointCategory: 'weatherPoint',
    },
    bee_hours: {
      valueType: 'beeHours_last24hoursSum_hours',
      mItem: 'M_InBlockPoint',
      pointCategory: 'weatherPoint',
    },
    chill: {
      valueType: 'chillPortions_sinceNov1Sum_portions',
      mItem: 'M_InBlockPoint',
      pointCategory: 'weatherPoint',
    },
    dew_point: {
      valueType: 'dewPoint_in_c',
      mItem: 'M_InBlockPoint',
      pointCategory: 'weatherPoint',
      outOfBlockValueType: 'dewPoint_out_c',
    },
    humidity: {
      valueType: 'humidity_in_pct',
      mItem: 'M_InBlockPoint',
      pointCategory: 'weatherPoint',
      outOfBlockValueType: 'humidity_out_pct',
    },
    leaf_wetness: {
      valueType: 'leafWetness_last24HoursSum_wetHours',
      mItem: 'M_InBlockPoint',
      pointCategory: 'weatherPoint',
    },
    wet_bulb: {
      valueType: 'wetBulb_in_c',
      mItem: 'M_InBlockPoint',
      pointCategory: 'weatherPoint',
      outOfBlockValueType: 'wetBulb_out_c',
    },
    fruit_growth: { valueType: 'fruitSize_um', mItem: 'M_InBlockPoint', pointCategory: 'fruitGrowthPoint' },
    plant_stress: {
      valueType: 'plantStress_yesterday_signalTS5',
      mItem: 'M_InBlockPoint',
      pointCategory: 'plantStressPoint',
    },
    // DEGREE DAYS
    ...degreeDaysRecord((insectId) => initialStateDegreeDays(insectId, 'M_Block')),
    // @ts-expect-error // TODO: how should we reconcile these worlds?
    soil: { valueType: 'DEFAULT', mItem: 'M_InBlockPoint', pointCategory: 'soilPoint' },
    irrigation_activity_pressure: {
      valueType: 'irrigationIsOn_status',
      mItem: 'M_InBlockPoint',
      pointCategory: 'pressureTransducerPoint',
    },
    irrigation_activity_flow_meters: {
      valueType: 'irrigationActivity_today_cumulativeFlowMeter_gallons',
      mItem: 'M_InBlockPoint',
      pointCategory: 'flowMeterDevice',
    },
    precipitation: {
      valueType: 'precipitation_next24HoursSum_mm',
      mItem: 'M_OutOfBlockPoint',
      pointCategory: 'outOfBlockPoint',
    },
    wind: { valueType: 'windSpeed_mps', mItem: 'M_OutOfBlockPoint', pointCategory: 'outOfBlockPoint' },
    wind_machine: {
      valueType: 'windMachine_temperature_c',
      mItem: 'M_InBlockPoint',
      pointCategory: 'windMachinePoint',
    },
    fire_blight: {
      valueType: 'fireBlight_today_trv',
      mItem: 'M_InBlockPoint',
      pointCategory: 'weatherPoint',
    },
  },
  [MAP_VISUAL.BLOCK]: {
    ...emptyNonInsects,
    ...emptyInsectLarvalTrend,
    ...emptyInsectPhenology,
    air_temperature: { valueType: 'temperature_minMedianMax_in_c', mItem: 'M_Block' },
    almond_bloom: { valueType: 'almondBloom_today_pct', mItem: 'M_Block' },
    almond_hull_split: { valueType: 'almondHullSplit_date', mItem: 'M_Block' },
    bee_hours: { valueType: 'beeHours_last24hoursSum_hours', mItem: 'M_Block' },
    cherry_powdery_mildew: { valueType: 'cherryPowderyMildewModelPredictedRisk_today', mItem: 'M_Block' },
    chill: { valueType: 'chillPortions_sinceNov1SumMinMedianMax_portions', mItem: 'M_Block' },
    dew_point: { valueType: 'dewPoint_minMedianMax_in_c', mItem: 'M_Block' },
    evapotranspiration: { valueType: 'evapotranspirationETc_next7DaysSum_mm', mItem: 'M_Block' },
    grape_powdery_mildew: { valueType: 'grapePowderyMildewModelPredictedRisk_today', mItem: 'M_Block' },
    humidity: { valueType: 'humidity_minMedianMax_in_pct', mItem: 'M_Block' },
    spray_conditions: { valueType: 'sprayConditions_currentHour', mItem: 'M_Property' },
    wet_bulb: { valueType: 'wetBulb_minMedianMax_in_c', mItem: 'M_Block' },
    leaf_wetness: { valueType: 'leafWetness_last24HoursSum_wetHours', mItem: 'M_Block' },
    // DEGREE DAYS
    ...degreeDaysRecord((insectId) => initialStateDegreeDaysSpatial(insectId, 'M_Block')),
    // TRAP CATCHES
    ...trapCatchesRecord((insectId) => initialStateTrapCatches(insectId, 'M_Trap')),
    // DISEASES
    alternaria: { valueType: 'alternariaCumulativeRolling7Days_tomorrow_dsv', mItem: 'M_Block' },
    fire_blight: { valueType: 'fireBlight_today_trv', mItem: 'M_Block' },
  },
  [MAP_VISUAL.PROPERTY]: {
    ...emptyNonInsects,
    ...emptyInsectTrapCatches,
    ...emptyInsectLarvalTrend,
    ...emptyInsectPhenology,
    air_temperature: { valueType: 'temperature_minMedianMax_in_c', mItem: 'M_Property' },
    almond_hull_split: { valueType: 'almondHullSplitMedian_date', mItem: 'M_Property' },
    bee_hours: { valueType: 'beeHours_last24hoursSumMinMedianMax_hours', mItem: 'M_Property' },
    chill: { valueType: 'chillPortions_sinceNov1SumMinMedianMax_portions', mItem: 'M_Property' },
    dew_point: { valueType: 'dewPoint_minMedianMax_in_c', mItem: 'M_Property' },
    humidity: { valueType: 'humidity_minMedianMax_in_pct', mItem: 'M_Property' },
    irrigation_activity_pressure: { valueType: 'irrigationIsOn_status', mItem: 'M_Property' },
    irrigation_activity_zones: {
      valueType: 'irrigationIsOn_status',
      mItem: 'M_Property',
    },
    leaf_wetness: { valueType: 'leafWetness_last24HoursSum_wetHours', mItem: 'M_Property' },
    precipitation: { valueType: 'precipitation_next24HoursSumMinMedianMax_mm', mItem: 'M_Property' },
    spray_conditions: { valueType: 'sprayConditions_currentHour', mItem: 'M_Property' },
    wet_bulb: { valueType: 'wetBulb_minMedianMax_in_c', mItem: 'M_Property' },
    wind: { valueType: 'windSpeed_minMedianMax_mps', mItem: 'M_Property' },
    // DEGREE DAYS
    ...degreeDaysRecord((insectId) => initialStateDegreeDaysSpatialProperty(insectId, 'M_Property')),
    // TRAP CATCHES
    ...trapCatchesRecord((insectId) => initialStateTrapCatchesProperty(insectId, 'M_Property')),
    // DISEASES
    alternaria: { valueType: 'alternariaCumulativeRolling7DaysMinMedMax_tomorrow_dsv', mItem: 'M_Property' },
    cherry_powdery_mildew: { valueType: 'cherryPowderyMildewModelPredictedRisk_today', mItem: 'M_Property' },
    grape_powdery_mildew: { valueType: 'grapePowderyMildewModelPredictedRisk_today', mItem: 'M_Property' },
    fire_blight: { valueType: 'fireBlight_today_trv', mItem: 'M_Property' },
  },
  [MAP_VISUAL.SCD]: {
    ...emptyNonInsects,
    ...emptyInsectLarvalTrend,
    ...emptyInsectTrapCatches,
    ...emptyInsectPhenology,
    air_temperature: { valueType: 'temperature_in_c', mItem: 'M_SCD' },
    almond_hull_split: { valueType: 'almondHullSplit_date', mItem: 'M_SCD' },
    chill: { valueType: 'chillPortions_sinceNov1Sum_portions', mItem: 'M_SCD' },
    dew_point: { valueType: 'dewPoint_in_c', mItem: 'M_SCD' },
    evapotranspiration: { valueType: 'evapotranspirationETc_next7DaysSum_mm', mItem: 'M_SCD' },
    humidity: { valueType: 'humidity_in_pct', mItem: 'M_SCD' },
    wet_bulb: { valueType: 'wetBulb_in_c', mItem: 'M_SCD' },
    fire_blight: { valueType: 'fireBlight_today_trv', mItem: 'M_SCD' },
    // DEGREE DAYS
    ...degreeDaysRecord((insectId) => initialStateDegreeDays(insectId, 'M_SCD')),
  },
  [MAP_VISUAL.TRAP]: {
    ...emptyNonInsects,
    ...emptyInsectLarvalTrend,
    ...emptyInsectPhenology,
    ...emptyInsectDegreeDays,
    ...trapCatchesRecord((insectId) => initialStateTrapCatches(insectId, 'M_Trap')),
  },
  [MAP_VISUAL.IRRIGATION_ZONE]: {
    ...emptyNonInsects,
    ...emptyInsectDegreeDays,
    ...emptyInsectLarvalTrend,
    ...emptyInsectPhenology,
    ...emptyInsectTrapCatches,
    irrigation_activity_zones: {
      valueType: 'irrigationIsOn_status',
      mItem: 'M_IrrigationZone',
    },
  },
  [MAP_VISUAL.REGION]: {
    ...emptyNonInsects,
    ...emptyInsectDegreeDays,
    ...emptyInsectLarvalTrend,
    ...emptyInsectTrapCatches,
    ...emptyInsectPhenology,
    ...degreeDaysRecord((insectId) => initialStateDegreeDaysSpatial(insectId, 'M_Region')),
    air_temperature: { valueType: 'temperature_minMedianMax_in_c', mItem: 'M_Region' },
    dew_point: { valueType: 'dewPoint_minMedianMax_in_c', mItem: 'M_Region' },
    humidity: { valueType: 'humidity_minMedianMax_in_pct', mItem: 'M_Region' },
    precipitation: { valueType: 'precipitation_next24HoursSumMinMedianMax_mm', mItem: 'M_Region' },
    wet_bulb: { valueType: 'wetBulb_minMedianMax_in_c', mItem: 'M_Region' },
    wind: { valueType: 'windSpeed_minMedianMax_mps', mItem: 'M_Region' },
    leaf_wetness: { valueType: 'leafWetness_last24HoursSum_wetHours', mItem: 'M_Region' },
  },
  [MAP_VISUAL.IMAGE_OVERLAY]: {
    ...emptyNonInsects,
    ...emptyInsectDegreeDays,
    ...emptyInsectLarvalTrend,
    ...emptyInsectTrapCatches,
    ...emptyInsectPhenology,
    ndvi: {
      valueType: 'imageryOverlayNDVI_current',
      mItem: 'M_Block',
    },
  },
}
